import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion, useInView } from "framer-motion";
import { cn } from "@/lib/utils";

const EASE = [0.22, 1, 0.36, 1];

export const Container = ({ className, children }) => (
  <div className={cn("mx-auto w-full max-w-shell px-6 md:px-12", className)}>
    {children}
  </div>
);

export const Reveal = ({
  children,
  delay = 0,
  y = 26,
  once = true,
  as = "div",
  className,
}) => {
  const reduce = useReducedMotion();
  const M = motion[as] || motion.div;
  return (
    <M
      initial={reduce ? false : { opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, margin: "-70px" }}
      transition={{ duration: 0.75, delay, ease: EASE }}
      className={className}
    >
      {children}
    </M>
  );
};

// Masked line-by-line reveal (runs on mount)
export const MaskText = ({
  lines = [],
  className,
  lineClassName,
  delay = 0,
  stagger = 0.09,
  animateOnView = false,
}) => {
  const reduce = useReducedMotion();
  const trigger = animateOnView
    ? { whileInView: { y: "0%" }, viewport: { once: true } }
    : { animate: { y: "0%" } };

  return (
    <span className={className}>
      {lines.map((line, i) => (
        <span className="mask-line" key={i}>
          <motion.span
            className={cn("block", lineClassName)}
            initial={reduce ? false : { y: "115%" }}
            {...trigger}
            transition={{
              duration: 0.9,
              delay: delay + i * stagger,
              ease: EASE,
            }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </span>
  );
};

// Image reveal via clip-path mask
export const ImageReveal = ({ src, alt, className, imgClassName }) => {
  const reduce = useReducedMotion();
  return (
    <div className={cn("overflow-hidden", className)}>
      <motion.img
        src={src}
        alt={alt}
        loading="lazy"
        initial={reduce ? false : { scale: 1.15, clipPath: "inset(100% 0 0 0)" }}
        whileInView={{ scale: 1, clipPath: "inset(0% 0 0 0)" }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 1.1, ease: EASE }}
        className={cn("h-full w-full object-cover", imgClassName)}
      />
    </div>
  );
};

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

export const Counter = ({ value, suffix = "", className }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const match = String(value).match(/^(\D*)(\d+)(.*)$/) || ["", "", "0", ""];
  const prefix = match[1] || "";
  const target = parseInt(match[2], 10) || 0;
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const dur = 1500;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      setN(Math.round(easeOutCubic(p) * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, target]);

  return (
    <span ref={ref} className={className}>
      {prefix}
      {n}
      {suffix}
    </span>
  );
};
